import { A, useLocation, useNavigate } from "@solidjs/router";
import { For, Show, createMemo } from "solid-js";
import { INBOX_SLUG, projectDisplay } from "../lib/tags";
import { deleteProject, tickets } from "../store/tickets";
import { drawerOpen, setDrawerOpen, setQuickAddOpen } from "../store/ui";
import styles from "./Sidebar.module.css";

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const close = () => setDrawerOpen(false);

  const openQuickAdd = () => {
    setQuickAddOpen(true);
    close();
  };

  const projects = createMemo(() => {
    const set = new Set<string>();
    for (const t of tickets()) if (t.project) set.add(t.project);
    return [...set].sort();
  });

  const handleDeleteProject = async (name: string) => {
    const count = tickets().filter((t) => t.project === name).length;
    const ok = window.confirm(
      `刪除專案「${projectDisplay(name)}」?底下 ${count} 張工單會退回未分類。`,
    );
    if (!ok) return;
    try {
      await deleteProject(name);
    } catch {
      // deleteProject already refetched (finally) so the UI reflects whatever
      // actually committed; just tell the user it was partial.
      window.alert("刪除專案時發生錯誤,部分工單可能未移動。畫面已重新整理。");
      return;
    }
    // If we were viewing the project we just dissolved, its filtered list is
    // now empty with no way out — bail to Today. location.pathname is encoded;
    // decode before comparing against the (possibly non-ASCII) slug.
    let decoded = location.pathname;
    try {
      decoded = decodeURIComponent(location.pathname);
    } catch {
      /* malformed escape — fall back to the raw path */
    }
    if (decoded === `/project/${name}`) navigate("/today");
  };

  return (
    <aside class={styles.sidebar} classList={{ [styles.open]: drawerOpen() }}>
      <div class={styles.brand}>
        <div class={styles.logo} />
        <span>Backlog</span>
      </div>

      <button type="button" class={styles.newButton} onClick={openQuickAdd}>
        <span class={styles.newPlus}>+</span>
        快速新增
        <kbd class={styles.newKbd}>c</kbd>
      </button>

      <nav class={styles.section}>
        <A
          href="/today"
          class={styles.item}
          activeClass={styles.itemActive}
          onClick={close}
        >
          Today
        </A>
        <A
          href="/list"
          class={styles.item}
          activeClass={styles.itemActive}
          onClick={close}
          end
        >
          All issues
        </A>
        <A
          href="/tree"
          class={styles.item}
          activeClass={styles.itemActive}
          onClick={close}
        >
          Tree
        </A>
      </nav>

      <div class={styles.section}>
        <div class={styles.sectionLabel}>Projects</div>
        <For each={projects()}>
          {(p) => (
            <div class={styles.projectRow}>
              <A
                href={`/project/${p}`}
                class={`${styles.item} ${styles.projectLink}`}
                activeClass={styles.itemActive}
                onClick={close}
              >
                <span
                  class={styles.dot}
                  style={{ background: "var(--c-accent)" }}
                />
                {projectDisplay(p)}
              </A>
              <Show when={p !== INBOX_SLUG}>
                <button
                  type="button"
                  class={styles.projectDelete}
                  onClick={() => handleDeleteProject(p)}
                  aria-label={`刪除專案 ${projectDisplay(p)}`}
                >
                  ×
                </button>
              </Show>
            </div>
          )}
        </For>
      </div>
    </aside>
  );
}
